import { Card } from '../components/Card'
import { Icon, type IconName } from '../components/Icon'
import { useTranslation } from 'react-i18next'

interface Template {
  name: string
  desc: string
  icon: IconName
  tone: 'mint' | 'royal' | 'flame' | 'sky2' | 'honey' | 'plum'
  rows: string
}

const TEMPLATES: Template[] = [
  { name: 'Carrier P&L Report',         desc: 'Multi-tab Excel with revenue, cost, margin per carrier — month, week, custom range.', icon: 'file-bar-chart',     tone: 'mint',  rows: '~12k' },
  { name: 'Customer Statement',         desc: 'Per-customer rollup: shipments, charges, rebills, balance. Net 14 / Net 30 templates.', icon: 'user-square',        tone: 'royal', rows: '~5k' },
  { name: 'Dispute Pack',               desc: 'Pre-filled cover letter + dispute table + supporting evidence sheet (per carrier).',     icon: 'scale',              tone: 'flame', rows: '~2k' },
  { name: 'Reconciliation Report',      desc: 'API charges vs invoiced rows for any carrier · any period. Diff highlighting.',           icon: 'git-compare',        tone: 'sky2',  rows: '~30k' },
  { name: 'Rate Card · Partner View',   desc: 'ByeLabel partner rate card — USPS T4 under 1lb, GOFO + $1.50 over 1lb.',                    icon: 'badge-dollar-sign', tone: 'honey', rows: '~150' },
  { name: 'Customs Rate Table',         desc: 'HS code → duty% lookup table. JSON + docx version. Auto-refreshed weekly.',                icon: 'globe',              tone: 'plum',  rows: '~8k' },
  { name: 'DHL Leakage Findings',       desc: 'PHL0/PHLI split, DIM excess, billing lag, aged orphans. Multi-sheet.',                      icon: 'plane',              tone: 'flame', rows: '~3k' },
  { name: 'GOFO Concierge Projection',  desc: 'Customer-level pricing model with 50/50 split, Printanem TX scenario.',                     icon: 'compass',            tone: 'mint',  rows: '~1k' },
]

export function ExportsView() {
  const { t } = useTranslation()
  return (
    <div>
      <div className="mb-10">
        <div className="text-[12px] font-semibold text-mint-600 uppercase tracking-wider">Module 5</div>
        <h1 className="text-[32px] font-bold tracking-tight mt-2">{t('exports.title')}</h1>
        <p className="text-ink-500 text-[14px] mt-2">{t('exports.subtitle')}</p>
      </div>

      <Card className="p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[15px] font-semibold">{t('exports.template_library')}</div>
            <div className="text-[12px] text-ink-500 mt-0.5">{t('exports.template_sub')}</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {TEMPLATES.map(t => (
            <Card key={t.name} hover className="p-4 cursor-pointer">
              <div className={`w-10 h-10 rounded-xl grid place-items-center bg-${t.tone}-50`}>
                <Icon name={t.icon} className={`w-5 h-5 text-${t.tone}-600`} />
              </div>
              <div className="text-[14.5px] font-semibold mt-3 leading-snug">{t.name}</div>
              <div className="text-[12.5px] text-ink-500 mt-1 leading-snug">{t.desc}</div>
              <div className="flex items-center gap-2 mt-3 text-[11.5px] text-ink-500">
                <span className="pill pill-ink">Typical: {t.rows} rows</span>
                <span className="ml-auto inline-flex items-center gap-1 text-mint-600 font-semibold">Open <Icon name="arrow-right" className="w-3 h-3" /></span>
              </div>
            </Card>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="text-[15px] font-semibold mb-1">{t('exports.quick_builder')}</div>
        <div className="text-[12px] text-ink-500 mb-4">{t('exports.quick_builder_sub')}</div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Source">
            <select className="mt-1 w-full h-9 px-3 border border-ink-200 rounded-lg text-[13.5px] bg-white">
              <option>byelabel_shipments</option>
              <option>usps_transaction_details</option>
              <option>dhl_invoices</option>
              <option>customer_rebills</option>
              <option>carrier_disputes</option>
            </select>
          </Field>
          <Field label="Period">
            <select className="mt-1 w-full h-9 px-3 border border-ink-200 rounded-lg text-[13.5px] bg-white">
              <option>YTD 2026</option><option>Last 30 days</option><option>April 2026</option><option>Custom…</option>
            </select>
          </Field>
          <Field label="Group by">
            <select className="mt-1 w-full h-9 px-3 border border-ink-200 rounded-lg text-[13.5px] bg-white">
<option>{t('exports.group_carrier')}</option><option>{t('exports.group_customer')}</option><option>{t('exports.group_reason')}</option><option>{t('exports.group_week')}</option>
            </select>
          </Field>
          <Field label="Currency">
            <select className="mt-1 w-full h-9 px-3 border border-ink-200 rounded-lg text-[13.5px] bg-white">
              <option>USD (canonical)</option><option>CAD</option><option>GBP</option><option>EUR</option><option>Native (per row)</option>
            </select>
          </Field>
        </div>

        <div className="flex items-center gap-2 pt-4 border-t border-ink-100">
          <div className="text-[12.5px] text-ink-500">Estimated rows: <span className="font-semibold text-ink-900 num">~12,488</span> · Estimated size: <span className="font-semibold text-ink-900">2.4 MB</span></div>
          <button className="ml-auto h-9 px-4 rounded-lg bg-mint-500 text-white font-semibold inline-flex items-center gap-2"><Icon name="file-down" /> Generate Excel</button>
        </div>
        <div className="mt-3 text-[12px] text-ink-500">
          <Icon name="info" className="w-3.5 h-3.5 inline -mt-0.5" /> Generation needs Redis (BullMQ) — not yet running. Reads above are live from your local PG.
        </div>
      </Card>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  )
}
